import React from 'react';
import { Maximize2, Minimize2, Copy, Check, UserX } from 'lucide-react';
import './Lobby.css';

function Lobby({ socket, lobby, lobbyCode, playerName, toggleFullscreen, isFullscreen, onLeave }) {
  const [copied, setCopied] = React.useState(false);
  const [showQR, setShowQR] = React.useState(false);
  const isHost = lobby && socket && lobby.host === socket.id;
  
  // Debug logging
  React.useEffect(() => {
    if (lobby && lobby.players) {
      console.log('Lobby.js: Rendering with players:', lobby.players.map(p => ({ name: p.name, multiplier: p.multiplier })));
    }
  }, [lobby]);

  const copyCode = () => {
    navigator.clipboard.writeText(lobbyCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleKick = (playerId) => {
    if (isHost) {
      socket.emit('kickPlayer', { code: lobbyCode, playerId });
    }
  };

  const handleStartGame = () => {
    if (isHost && lobby.players.length >= 2) {
      socket.emit('startGame', lobbyCode);
      // Track game start
      if (typeof window.trackGameStart === 'function') {
        window.trackGameStart(lobbyCode);
      }
    }
  };

  const handleMultiplierChange = (value) => {
    console.log('handleMultiplierChange called:', { value, lobbyCode });
    socket.emit('setMultiplier', { code: lobbyCode, multiplier: parseInt(value) });
  };

  return (
    <div className="lobby-container">
      <div className="lobby-header">
        <div className="lobby-title">
          <h1>LOBBY</h1>
          <div className="lobby-code-display">
            <span className="code-label">Code:</span>
            <span className="code-value">{lobbyCode}</span>
            <button onClick={copyCode} className="copy-btn">
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
            <button onClick={() => setShowQR(true)} className="copy-btn" title="Show QR code">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm0 11h7v7h-7v-7zM3 14h7v7H3v-7z"/></svg>
            </button>
          </div>
        </div>
        <button onClick={toggleFullscreen} className="fullscreen-btn">
          {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
        </button>
      </div>

      <div className="lobby-content">
        <div className="players-section">
          <h2>Players ({lobby?.players.length || 0})</h2>
          <div className="players-grid">
            {lobby?.players.map((player, index) => (
              <div key={player.id} className="player-card">
                <div className="player-info">
                  <div className="player-avatar">
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="player-name">
                    {player.name} {player.id === lobby.host && '(Host)'}
                  </span>
                  {isHost && player.id !== lobby.host && (
                    <button
                      onClick={() => handleKick(player.id)}
                      className="kick-btn"
                      title="Kick player"
                    >
                      <UserX size={16} />
                    </button>
                  )}
                </div>
                <div className="player-multiplier">
                  <label>Multiplier: </label>
                  <select
                    value={player.multiplier || 1}
                    onChange={(e) => {
                      console.log('Multiplier onChange:', { playerName: player.name, currentUser: playerName, match: player.name === playerName, value: e.target.value, currentMultiplier: player.multiplier });
                      if (player.name === playerName) {
                        handleMultiplierChange(e.target.value);
                      }
                    }}
                    disabled={player.name !== playerName}
                    className="multiplier-select"
                  >
                    {console.log('Rendering select for', player.name, 'with multiplier:', player.multiplier)}
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num}x</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>

        {isHost && (
          <div className="lobby-settings">
            <h3>Game Settings</h3>
            <div className="settings-grid">
              <div className="setting-item">
                <label>Game Mode:</label>
                <select
                  value={lobby?.gameMode || 'normal'}
                  onChange={(e) => socket.emit('setGameMode', { code: lobbyCode, gameMode: e.target.value })}
                  className="setting-select"
                >
                  <option value="normal">Normal (All Phases)</option>
                  <option value="pyramidOnly">Pyramid Only</option>
                  <option value="busOnly">Bus Only</option>
                </select>
              </div>
              <div className="setting-item">
                <label>Pyramid Timer (seconds):</label>
                <select
                  value={lobby?.pyramidTimer || 5}
                  onChange={(e) => socket.emit('setPyramidTimer', { code: lobbyCode, timer: parseInt(e.target.value) })}
                  className="setting-select"
                >
                  {[3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num}s</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="lobby-actions">
          {onLeave && (
            <button onClick={onLeave} className="btn btn-ghost" style={{color:'rgba(255,255,255,0.35)',fontSize:'0.8rem',marginBottom:'0.5rem'}}>
              ← Leave Lobby
            </button>
          )}
          {isHost ? (
            <>
              <button
                onClick={handleStartGame}
                className="btn btn-start"
                disabled={lobby?.players.length < 2}
              >
                Start Game
              </button>
              {lobby?.players.length < 2 && (
                <p className="warning-text">Need at least 2 players to start</p>
              )}
            </>
          ) : (
            <p className="waiting-text">Waiting for host to start the game...</p>
          )}
        </div>

        <div className="game-info">
          <h3>Game Rules</h3>
          <div className="rules-list">
            <div className="rule-item">
              <span className="rule-number">1</span>
              <div className="rule-content">
                <h4>Phase 1: Four Questions</h4>
                <p>Red or Black? Higher or Lower? Inside or Outside? Guess the Suit!</p>
              </div>
            </div>
            <div className="rule-item">
              <span className="rule-number">2</span>
              <div className="rule-content">
                <h4>Phase 2: The Pyramid</h4>
                <p>Match your cards to the pyramid. Higher rows = more drinks to give!</p>
              </div>
            </div>
            <div className="rule-item">
              <span className="rule-number">3</span>
              <div className="rule-content">
                <h4>Phase 3: Ride the Bus</h4>
                <p>Player with most cards left must guess 4 cards correctly!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showQR && (
        <div style={{position:'fixed',inset:0,zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.8)',backdropFilter:'blur(8px)'}} onClick={() => setShowQR(false)}>
          <div style={{background:'white',borderRadius:'24px',padding:'32px',margin:'16px',textAlign:'center',maxWidth:'360px',width:'100%'}} onClick={e => e.stopPropagation()}>
            <h3 style={{fontSize:'20px',fontWeight:'bold',color:'#1f2937',marginBottom:'8px'}}>Scan to Join</h3>
            <p style={{color:'#6b7280',fontSize:'14px',marginBottom:'16px'}}>Code: <strong style={{color:'#e11d48'}}>{lobbyCode}</strong></p>
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(`https://www.partygamez.com/ridethebus/?code=${lobbyCode}`)}`} alt="QR Code" style={{width:'256px',height:'256px',margin:'0 auto 16px'}} />
            <button onClick={() => setShowQR(false)} style={{padding:'8px 24px',background:'#e5e7eb',borderRadius:'12px',fontWeight:'600',border:'none',cursor:'pointer'}}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Lobby;
